/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { ICSubscribeProps } from './c-subscribe.props.autogenerated';
import { ICSubscribeData } from './c-subscribe.data';
import { SharestateInput } from '../../actions/newsletter-share-state.action';
import { validateEmail } from '../utilities/helper';

/**
 * Subscribe view props.
 */
export interface ISubscribeViewProps extends ICSubscribeProps<ICSubscribeData> {
    subscribe: IModuleProps;
    subscribeContainer: INodeProps;
    heading: React.ReactNode;
    text: React.ReactNode;
    submitButton?: INodeProps;
    submitButtonLabelText?: string;
    subscribeForm?: INodeProps;
    emailInput?: INodeProps;
    handleText(): void;
    onSubmit(event: React.FormEvent<HTMLFormElement>): void; //VSI Customization
}

/**
 * VSI- Customization
 * Toggle State for Script Modal
 */
export interface ISubscribeState {
    isEmailValid: boolean;
}

/**
 * Subscribe component.
 * @extends {React.PureComponent<ICSubscribeProps<ICSubscribeData>>}
 */
export class Subscribe extends React.PureComponent<ICSubscribeProps<ICSubscribeData>, ISubscribeState> {
    private readonly _emailAddressInputRef: React.RefObject<HTMLInputElement>;

    public constructor(props: ISubscribeViewProps) {
        super(props);
        this._emailAddressInputRef = React.createRef();
        this.state = {
            isEmailValid: false
        };
    }

    public render(): JSX.Element | null {
        const { heading, paragraph } = this.props.config;
        // const { isEmailValid } = this.state;
        const moduleClassName = 'ms-subscribe';

        const subscribeParagraph = paragraph && (
            <Msdyn365.RichTextComponent
                text={paragraph}
                className='subscribe-label mb-24'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );

        const subscribeHeading = heading && (
            <Msdyn365.Text
                className='ms-subscribe__details__heading'
                tag={heading.tag ?? 'h1'}
                text={heading.text}
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );
        const viewProps = {
            ...this.props,
            handleText: this.handleTextChange,
            subscribe: {
                moduleProps: this.props,
                className: moduleClassName
            },
            subscribeContainer: {
                className: 'ms-subscribe__details'
            },
            heading: subscribeHeading,
            text: subscribeParagraph,
            subscribeForm: {
                // className: 'ms-subscribe__details__form',
                tag: 'form',
                'aria-label': this.props.resources.subscribeFormAriaLabel,
                name: 'subscribeForm',
                role: 'form',
                autoComplete: 'off'
            },
            emailInput: {
                className: 'ms-subscribe__details__form__email',
                tag: 'input',
                ref: this._emailAddressInputRef,
                type: 'email',
                'aria-label': this.props.resources.subscribeFormEmailAriaLabel,
                value: this.props.data.newsletterShareState.result?.email,
                placeholder: this.props.resources.subscribeEmailPlaceholderText,
                onChange: this._handleEmailInput
            },
            submitButton: {
                className: 'subscribe-btn text-white py-16 px-30',
                tag: Button,
                'aria-label': this.props.resources.subscribeFormSubmitAriaLabel,
                type: 'submit',
                title: this.props.resources.subscribeButtonLabel
                // disabled: !isEmailValid
            },
            // submitButtonLabelText: this.props.resources.subscribeButtonLabel,
            submitButtonLabelText: 'Subscribe',
            onSubmit: this._onSubmit //VSI Cutomization
        };

        return this.props.renderView(viewProps);
    }

    /**
     * Handle text change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleTextChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handle paragraph change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.paragraph = event.target.value;
    };

    /**
     * VSI Customization - START
     */
    private _openScriptModal = (): void => {
        if (this.props.data.newsletterShareState.result) {
            this.props.context.actionContext.update(new SharestateInput(), {
                ...this.props.data.newsletterShareState.result,
                isModalOpen: true
            });
        }
    };

    private _handleEmailInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (this.props.data.newsletterShareState.result) {
            this.props.context.actionContext.update(new SharestateInput(), {
                ...this.props.data.newsletterShareState.result,
                email: e.target.value
            });
        }

        if (validateEmail(e.target.value) === true) {
            this.setState({ isEmailValid: true });
        } else {
            this.setState({ isEmailValid: false });
        }
    };

    /**
     * Override form action and button action below.
     * @param event - The form event.
     */
    private _onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        const isConsentGiven =
            this.props.context.request &&
            this.props.context.request.cookies &&
            this.props.context.request.cookies.isConsentGiven &&
            this.props.context.request.cookies.isConsentGiven();
        // if (this.props.data.newsletterShareState.result && isConsentGiven) {
        //     const { email } = this.props.data.newsletterShareState.result;
        //     const iframe = document.getElementById('ms-script-iframe') as HTMLIFrameElement;
        //     iframe.contentWindow?.postMessage(`email:${email}`, '*');
        // }
        if (isConsentGiven) {
            // const { email } = this.props.data.newsletterShareState.result;
            const iframe = document.getElementById('ms-script-iframe') as HTMLIFrameElement;
            iframe.contentWindow?.postMessage(`email:`, '*');
        }
        this._openScriptModal();
    };
    // VSI Customization - END
}

export default Subscribe;
